import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CheckCircle, XCircle, Mail, Clock, RefreshCw } from 'lucide-react';
import { useAuth } from '../App';
import { useToast } from '../hooks/use-toast';
import PromoCodePopup from './PromoCodePopup';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // verifying, success, error, expired
  const [message, setMessage] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [showPromoPopup, setShowPromoPopup] = useState(false);
  const [promoUserInfo, setPromoUserInfo] = useState(null);
  
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();
  
  const token = searchParams.get('token');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setVerificationStatus('error');
      setMessage('Invalid verification link. Please check your email for the correct link.');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      setVerificationStatus('verifying');
      
      const response = await fetch(`${backendUrl}/api/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok) {
        setVerificationStatus('success');
        setMessage(data.message);
        
        // Log the user in
        login(data.user);
        localStorage.setItem('dcTech_token', data.token);
        
        // Show promo popup if applicable (provider)
        if (data.showPromoPopup && data.user.userType === 'provider') {
          setPromoUserInfo({
            name: data.user.name,
            email: data.user.email
          });
          setShowPromoPopup(true);
        } else {
          // Redirect after a delay if no promo popup
          setTimeout(() => {
            if (data.user.userType === 'provider') {
              navigate('/subscription');
            } else {
              navigate('/dashboard');
            }
          }, 3000);
        }
      } else {
        setVerificationStatus('error');
        setMessage(data.detail || 'Email verification failed.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationStatus('error');
      setMessage('An error occurred during verification. Please try again.');
    }
  };

  const resendVerification = async () => {
    if (!resendEmail.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter your email address to resend verification.",
        variant: "destructive",
      });
      return;
    }

    setIsResending(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resendEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Verification Email Sent!",
          description: data.message,
        });
        setResendEmail('');
      } else {
        toast({
          title: "Error",
          description: data.detail || 'Failed to resend verification email.',
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Resend error:', error);
      toast({
        title: "Error",
        description: "An error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  const handlePromoGenerated = (promoData) => {
    toast({
      title: "Promo Code Sent! 🎉",
      description: `Your ${promoData.durationMonths}-month free Basic plan code has been sent to your email.`,
    });
  };

  const handlePromoPopupClose = () => {
    setShowPromoPopup(false);
    setPromoUserInfo(null);
    navigate('/subscription');
  };

  const getStatusIcon = () => {
    switch (verificationStatus) {
      case 'verifying':
        return <RefreshCw className="animate-spin text-blue-500" size={48} />;
      case 'success':
        return <CheckCircle className="text-green-500" size={48} />;
      case 'error':
        return <XCircle className="text-red-500" size={48} />;
      default:
        return <Mail className="text-gray-500" size={48} />;
    }
  };

  const getStatusColor = () => {
    switch (verificationStatus) {
      case 'verifying':
        return 'from-blue-500 to-blue-600';
      case 'success':
        return 'from-green-500 to-green-600';
      case 'error':
        return 'from-red-500 to-red-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-teal-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-white/10 backdrop-blur-lg border-white/20 shadow-2xl">
          <CardHeader className="text-center">
            <div className={`mx-auto mb-4 p-4 rounded-full bg-gradient-to-r ${getStatusColor()} w-fit`}>
              {getStatusIcon()}
            </div>
            <CardTitle className="text-white text-2xl">
              {verificationStatus === 'verifying' && 'Verifying Email...'}
              {verificationStatus === 'success' && 'Email Verified! 🎉'}
              {verificationStatus === 'error' && 'Verification Failed'}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="text-center">
              <p className="text-white/80 leading-relaxed">
                {message}
              </p>
            </div>

            {verificationStatus === 'success' && (
              <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-300 text-sm text-center">
                  {promoUserInfo ? 
                    'Welcome! You can now access your account.' :
                    'Redirecting you to your dashboard...'
                  }
                </p>
              </div>
            )}

            {verificationStatus === 'error' && (
              <div className="space-y-4">
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-300 text-sm">
                    If your verification link has expired or you're having trouble, 
                    you can request a new verification email below.
                  </p>
                </div>

                <div className="space-y-3">
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/60 focus:border-white/50 focus:outline-none"
                  />
                  
                  <Button
                    onClick={resendVerification}
                    disabled={isResending}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600   text-white"
                  >
                    {isResending ? (
                      <>
                        <RefreshCw className="animate-spin mr-2" size={16} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2" size={16} />
                        Resend Verification Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            <div className="text-center">
              <Button
                variant="outline"
                onClick={() => navigate('/auth')}
                className="border-white/50 text-white bg-white/10"
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Promo Code Popup */}
      {showPromoPopup && promoUserInfo && (
        <PromoCodePopup
          isOpen={showPromoPopup}
          onClose={handlePromoPopupClose}
          userInfo={promoUserInfo}
          onPromoGenerated={handlePromoGenerated}
        />
      )}
    </div>
  );
};

export default EmailVerification;